import { useTranslation } from "@sagot/i18n";
import { Skeleton, StatCard, type StatDelta } from "@sagot/ui";
import { formatNumber } from "../shared/format";
import type { ChannelOut } from "../shared/types";
import { channelKpis, formatResponse } from "./channels-model";
import type { SummaryOut } from "./use-channels";

const P = "deployKnowledge.channels.kpi";

function conversationsDelta(summary: SummaryOut | undefined, t: (key: string, options?: Record<string, unknown>) => string): StatDelta | undefined {
  const pct = summary?.conversations_delta_pct;
  if (pct === null || pct === undefined) return undefined;
  const value = Math.round(pct);
  return { direction: value >= 0 ? "up" : "down", positive: value >= 0, label: t(value >= 0 ? `${P}.deltaUp` : `${P}.deltaDown`, { value }) };
}

export function ChannelKpis({ channels, summary }: { channels: readonly ChannelOut[]; summary: SummaryOut | undefined }) {
  const { t, i18n } = useTranslation();
  const kpis = channelKpis(channels);
  const delta = conversationsDelta(summary, t);
  return (
    <section className="grid grid-cols-2 gap-4 md:grid-cols-4" aria-label={t("deployKnowledge.channels.title")}>
      <StatCard label={t(`${P}.active`)} value={kpis.active} footnote={t(`${P}.activeOf`, { count: kpis.available })} />
      <StatCard
        label={t(`${P}.conversations`)}
        value={formatNumber(kpis.conversations, i18n.language)}
        delta={delta}
        footnote={delta ? undefined : t(`${P}.noDelta`)}
      />
      <StatCard
        label={t(`${P}.top`)}
        value={<span className="text-lg">{kpis.top ? t(`deployKnowledge.channels.names.${kpis.top.type}`) : t(`${P}.none`)}</span>}
        footnote={kpis.top ? t(`${P}.topShare`, { pct: kpis.top.pct }) : t(`${P}.noConversations`)}
      />
      <StatCard
        label={t(`${P}.avgResponse`)}
        value={<span className="text-lg font-mono">{formatResponse(summary?.avg_response_ms)}</span>}
        footnote={t(`${P}.avgResponseHint`)}
      />
    </section>
  );
}

export function ChannelKpisSkeleton() {
  return (
    <section className="grid grid-cols-2 gap-4 md:grid-cols-4" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="card card-body space-y-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-7 w-16" />
          <Skeleton className="h-3 w-20" />
        </div>
      ))}
    </section>
  );
}
